import { useMutation } from "@tanstack/vue-query";
import { useApi } from "./useApi";
import { useToaster } from "./useToaster";

interface UploadParams {
  file: File;
  collection: string;
  metadata?: Record<string, unknown>;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
}

function retryDelay(attemptIndex: number): number {
  return Math.min(1000 * Math.pow(2, attemptIndex), 10000);
}

export function useMediaUploadMutation() {
  const api = useApi();
  const toaster = useToaster();

  return useMutation({
    mutationFn: async (params: UploadParams) => {
      return await api.media.upload(params);
    },
    retry: (failureCount, error) => {
      const apiError = error as ApiError;
      
      // Don't retry validation errors
      if (apiError?.response?.status === 422) {
        return false;
      }
      
      // Retry network errors up to 3 times
      if (!apiError?.response) {
        return failureCount < 3;
      }
      
      // Retry rate limiting up to 2 times
      if (apiError?.response?.status === 429) {
        return failureCount < 2;
      }
      
      return false;
    },
    retryDelay,
    onError: (error) => {
      const apiError = error as ApiError;
      let message = "Upload failed after multiple attempts";
      
      if (apiError?.response?.status === 422) {
        message = apiError.response?.data?.message || "File validation failed";
      } else if (apiError?.response?.status === 429) {
        message = "Too many upload attempts. Please wait a moment and try again.";
      } else if (!apiError?.response) {
        message = "Upload failed. Please check your connection.";
      } else if (apiError?.response?.data?.message) {
        message = apiError.response.data.message;
      }
      
      toaster.create({
        title: message,
        type: "error",
      });
    },
  });
}



